import { useState } from 'react';
import { Modal, Form, Input, Select, Button, notification } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { requestIntegration } from '@/api/user';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';

const { TextArea } = Input;
const { Option } = Select;

const businessTypes = [
	'Retail',
	'Healthcare',
	'Technology',
	'Financial Services',
	'Education',
	'Manufacturing',
	'Hospitality',
	'Other',
];

const revenueRanges = [
	{ value: 'under100k', label: 'Under $100K' },
	{ value: '100k-500k', label: '$100K - $500K' },
	{ value: '500k-1M', label: '$500K - $1M' },
	{ value: '1M-5M', label: '$1M - $5M' },
	{ value: '5M-10M', label: '$5M - $10M' },
	{ value: 'over10M', label: 'Over $10M' },
];

interface RequestIntegrationModalProps {
	visible: boolean;
	onClose: () => void;
	userType?: string;
	loading?: boolean;
	form?: any;
	onSubmit?: (values: any) => void;
}

const RequestIntegrationModal: React.FC<RequestIntegrationModalProps> = ({
	visible,
	onClose,
	userType = 'Receptionist',
	loading: externalLoading,
	form: formProp,
	onSubmit,
}) => {
	const [form] = Form.useForm();
	const [businessTypeOther, setBusinessTypeOther] = useState(false);
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const integrationRequestMutation = useMutation({
		mutationFn: (payload: any) => requestIntegration(payload, userType),
		onSuccess: () => {
			notification.success({
				message: 'Integration Request Submitted',
				description:
					'Your integration request has been received. Our team will contact you shortly.',
			});
			onClose();
			form.resetFields();
		},
		onError: (error) => {
			console.error('Integration request error:', error);
			notification.error({
				message: 'Submission Failed',
				description: 'There was an error submitting your request. Please try again.',
			});
		},
	});

	const handleIntegrationSubmit = async (values: any) => {
		const payload = {
			...values,
			username: (selectedAgentId as string) || values.name,
		};
		integrationRequestMutation.mutate(payload);
		onSubmit && onSubmit(values);
	};

	return (
		<Modal
			title="Request an Integration"
			open={visible}
			onCancel={onClose}
			footer={null}
			maskClosable={false}
			destroyOnClose
		>
			<p className="text-gray-500 mb-4">
				Need integrations with your favorite tools and services? <br />
				Send us your request, and our team will review it and reach out to discuss the details.
			</p>
			<Form form={formProp || form} layout="vertical" onFinish={handleIntegrationSubmit}>
				<Form.Item
					name="name"
					label="Your name"
					rules={[{ required: true, message: 'Please enter your name' }]}
				>
					<Input placeholder="Please enter your name" />
				</Form.Item>

				<Form.Item
					name="businessType"
					label="Business Type"
					rules={[{ required: true, message: 'Please select your business type' }]}
				>
					<Select
						placeholder="Select your business type"
						onChange={(value) => setBusinessTypeOther(value.toLowerCase() === 'other')}
					>
						{businessTypes.map((item, index) => (
							<Option key={index} value={item.toLowerCase()}>
								{item}
							</Option>
						))}
					</Select>
				</Form.Item>

				{businessTypeOther && (
					<Form.Item
						name="businessTypeOther"
						label="Specify Business Type"
						rules={[{ required: true, message: 'Please specify your business type' }]}
					>
						<Input placeholder="Please specify your business type" />
					</Form.Item>
				)}

				<Form.Item
					name="businessName"
					label="Your business name"
					rules={[{ required: true, message: 'Please enter your business name' }]}
				>
					<Input placeholder="Please enter your business name" />
				</Form.Item>

				<Form.Item
					name="revenue"
					label="Annual Revenue"
					rules={[{ required: true, message: 'Please enter your annual revenue' }]}
				>
					<Select placeholder="Select revenue range">
						{revenueRanges.map((range, index) => (
							<Option key={index} value={range.value}>
								{range.label}
							</Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					name="requestedIntegration"
					label="Requested Integration"
					rules={[{ required: true, message: 'Please describe the integration you need' }]}
				>
					<TextArea
						rows={4}
						placeholder="Please describe the integration you need and how it would help your business"
					/>
				</Form.Item>

				<div className="flex justify-end gap-2 mt-4">
					<Button onClick={onClose}>Cancel</Button>
					<Button
						type="primary"
						htmlType="submit"
						loading={integrationRequestMutation.isPending || externalLoading}
					>
						Submit Request
					</Button>
				</div>
			</Form>
		</Modal>
	);
};

export default RequestIntegrationModal;
